import dayjs from 'dayjs';

import { Schemas } from '@api-client/generated/types';

/**
 * Whether a document carries an error, and how to say so.
 *
 * An error is a facet over a document's status, not one of its values: the BOB stuck watchdog and the
 * connector's reconciliation outcome land on documents that stay `processing`/`processed`. So the tag
 * and the card keep showing the real status and put a marker beside it, instead of a flagged document
 * having to choose which of the two things it is [ENG-1256].
 *
 * Deliberately mirrors `documentErrorFacet()` on the backend, including its second branch: a failed
 * BOB sync does not raise `has_error` — the connector writes `bob_status` and `adminNotes` instead —
 * so without it every failed document would lose its marker.
 *
 * The facts are spelled out rather than `Pick`ed from `Schemas.Document`: the generated type says
 * `errorCode: string` while the column is nullable (`@ApiProperty({ nullable: true })` on the DTO), so
 * a `Pick` would refuse the `null` that actually arrives.
 */
type DocumentErrorFacts = {
  adminStatus?: Schemas.Document['adminStatus'];
  bobStatus?: Schemas.Document['bobStatus'];
  hasError?: boolean | null;
  errorCode?: string | null;
  bobPendingAt?: string | null;
};

export const hasDocumentError = (
  document?: DocumentErrorFacts | null
): boolean =>
  Boolean(
    document &&
      (document.hasError ||
        (document.bobStatus === 'failed' && document.adminStatus === 'ready'))
  );

/**
 * The two codes the backend owns, spelled the way an accountant reads them. Anything else in
 * `errorCode` is an operator-facing label already ('Reupload file', 'bad_photo'), so it is shown as it
 * was written rather than guessed at.
 */
const OWNED_CODE_REASONS: Record<string, string> = {
  bob_sync_stuck: 'Stuck in the BOB queue',
  bob_reconciliation_required: 'Needs reconciliation in BOB50',
};

const describeCode = (errorCode?: string | null): string | null => {
  if (!errorCode) {
    return null;
  }

  return OWNED_CODE_REASONS[errorCode] ?? errorCode;
};

/** How long the document has been waiting for the connector, when it is waiting at all. */
const describeWait = (document: DocumentErrorFacts): string | null => {
  const isQueued =
    document.adminStatus === 'processing' && document.bobStatus === 'pending';

  if (!isQueued || !document.bobPendingAt) {
    return null;
  }

  return `in the BOB queue since ${dayjs(document.bobPendingAt).format('DD.MM.YYYY HH:mm')}`;
};

/**
 * One sentence for the marker's tooltip, or `null` when there is nothing to flag. The reason comes
 * first and the waiting time second, because the reason is what an operator acts on.
 */
export const getDocumentErrorReason = (
  document?: DocumentErrorFacts | null
): string | null => {
  if (!document || !hasDocumentError(document)) {
    return null;
  }

  const parts = [
    describeCode(document.errorCode) ??
      (document.bobStatus === 'failed'
        ? 'BOB sync failed'
        : 'Flagged with an error'),
    describeWait(document),
  ];

  return parts.filter(Boolean).join(' — ');
};

/**
 * A document the connector is about to book. Recognition is refused for this state (re-running it
 * would overwrite the accounting data), and the backend refuses it silently, so the UI has to know the
 * state rather than let an operator click into nothing [ENG-1261].
 */
export const isQueuedForBob = (document?: DocumentErrorFacts | null): boolean =>
  document?.adminStatus === 'processing' && document?.bobStatus === 'pending';

type RetryFacts = DocumentErrorFacts & { referenceId?: string | null };

/**
 * Whether an action may be offered, and what to say when it may not.
 *
 * Shared by every document action that a state can forbid, so a refusal always arrives as a disabled
 * control with a reason attached rather than as a control that quietly does nothing.
 */
export interface ActionEligibility {
  /** Whether the action may be sent. */
  allowed: boolean;
  /** Why not, when it may not — shown as the disabled control's tooltip. */
  blockedReason?: string;
}

/**
 * A document whose booking has already been recorded — by the connector (`bobStatus: 'succeeded'`) or
 * by an operator who entered it in BOB50 by hand. Its accounting data is what a real BOB50 booking was
 * made from, so it is the state that forbids the actions below.
 */
export const isBooked = (document?: DocumentErrorFacts | null): boolean =>
  document?.adminStatus === 'processed';

/**
 * Whether "Mark as booked" may be pressed for this document.
 *
 * Booking a booked document is not a second booking, it is a write of the status it already holds: the
 * backend plans no violation for it, commits the no-op, and the list answers "File information has
 * been successfully updated" — an operator is told a state changed when nothing did. The details
 * screen has always disabled the action for a booked document; the row menu and the bulk button did
 * not, which is the inconsistency this closes.
 */
export const getMarkAsBookedEligibility = (
  document?: DocumentErrorFacts | null
): ActionEligibility =>
  isBooked(document)
    ? {
        allowed: false,
        blockedReason:
          'Already booked. Booking it again changes nothing — reopen the document if its booking has to be corrected.',
      }
    : { allowed: true };

/**
 * The same rule over a selection, for the bulk button above the list.
 *
 * All-or-nothing, like the endpoint behind it: the batch is refused while it holds a booked document
 * rather than quietly booking the rest, so what the operator confirms is what is sent.
 *
 * Which is also why an unjudged row blocks the batch. The caller can only pass the documents the list
 * is showing, while the request carries every selected id — so a selection that outlived its rows
 * would be judged on a subset and sent in full, and a booked document among the ones nobody can see
 * would ride along in a batch that showed no reason to stop. `selectedCount` is how the rule learns
 * the difference; the list also clears the selection whenever it replaces the rows, so this is the
 * backstop rather than the everyday path.
 */
export const getBulkMarkAsBookedEligibility = (
  documents: readonly DocumentErrorFacts[],
  /** How many rows are selected, when that can be more than the caller could show. */
  selectedCount: number = documents.length
): ActionEligibility => {
  const booked = documents.filter((document) => isBooked(document)).length;

  // Named first when both hold: a booked document is the concrete thing to unselect, and saying so
  // beats sending the operator to re-make a selection whose real problem is one of its rows.
  if (booked > 0) {
    return {
      allowed: false,
      blockedReason: `Already booked: ${booked} of the ${selectedCount} selected. Booking again changes nothing — unselect ${booked === 1 ? 'it' : 'them'} first.`,
    };
  }

  if (documents.length < selectedCount) {
    return {
      allowed: false,
      blockedReason:
        'Some selected documents are no longer listed, so they cannot be checked before booking. Clear the selection and pick them again.',
    };
  }

  return { allowed: true };
};

/**
 * Whether "Restart recognition" may be pressed for this document.
 *
 * Two states forbid it, both because a fresh OCR pass would land on accounting data that something
 * else already owns:
 *
 *  - **in the BOB queue** — the connector is about to book from that data, and the backend refuses the
 *    re-recognition silently, so the operator used to click and be told nothing [ENG-1261];
 *  - **booked** — the booking already exists in BOB50. Here the backend does *not* refuse: its guard
 *    keys on the `processing`/`pending` pair, so recognition runs and overwrites the number, dates,
 *    amount and line data behind a booking that cannot be re-made from them. The accounting codes
 *    survive; the agreement between our record and BOB50 does not.
 */
export const getRecognitionEligibility = (
  document?: DocumentErrorFacts | null
): ActionEligibility => {
  if (isQueuedForBob(document)) {
    return {
      allowed: false,
      blockedReason:
        'Not while the document is in the BOB queue: recognition would overwrite the accounting data. Remove it from the queue first.',
    };
  }

  if (isBooked(document)) {
    return {
      allowed: false,
      blockedReason:
        'Not for a booked document: recognition would overwrite the data the BOB50 booking was made from, and the booking would no longer match it.',
    };
  }

  return { allowed: true };
};

/**
 * Whether "Retry BOB sync" should be offered for this document, and whether it may be pressed.
 *
 * Mirrors the endpoint: a booking id means BOB50 already holds this document, and a retry would book
 * it twice, so the backend answers 409 `ALREADY_BOOKED`. Showing the action disabled rather than
 * hiding it is deliberate — an operator looking at a flagged document needs to know that a retry
 * exists and why it is not available to them.
 *
 * `null` means the document is in no state a retry applies to, and the action is not rendered at all.
 */
export const getRetryEligibility = (
  document?: RetryFacts | null,
  /**
   * Whether the company is wired to a BOB dossier. Omitted where the caller cannot know — the rule
   * only ever narrows, so an unknown company never turns an allowed retry into a blocked one.
   */
  companyHasBobDossier?: boolean
): ActionEligibility | null => {
  if (!document) {
    return null;
  }

  const isRetryable =
    hasDocumentError(document) ||
    document.bobStatus === 'pending' ||
    document.bobStatus === 'failed';

  if (!isRetryable) {
    return null;
  }

  /**
   * A booked document is in no state a retry applies to, whatever `bobStatus` still says.
   *
   * Marking a document manually booked changes `adminStatus` and leaves `bobStatus` at `pending`, so
   * without this the action stayed on screen and — with no Doc ID to block it — stayed *enabled*: one
   * press would un-book a document a person has already entered in BOB50 by hand and hand it back to
   * the connector to book a second time. The `referenceId` rule below cannot catch it, because the
   * booking that happened outside the system is exactly the one with no id in ours.
   */
  if (document.adminStatus === 'processed') {
    return null;
  }

  const bookingId = document.referenceId?.trim();
  if (bookingId) {
    return {
      allowed: false,
      blockedReason: `Already booked in BOB50 as ${bookingId}. A retry would book it twice — reconcile it instead, or clear the Doc ID if it was entered by mistake.`,
    };
  }

  // Worse than a rejected request: a retry here *succeeds*. The document goes back into the queue and
  // its stuck timer restarts, but discovery skips a company with no `bob_reference_id`, so nothing
  // ever comes to collect it — the operator would be resetting a clock, not fixing anything.
  if (companyHasBobDossier === false) {
    return {
      allowed: false,
      blockedReason:
        'Automatic booking is disabled for this company — it has no BOB dossier, so the connector would never come to collect the document.',
    };
  }

  return { allowed: true };
};
